/**
 * Also-serving band (wayfinder L9 addendum #9) — the labeled home for
 * coverage-without-building on the Locations card.
 *
 * The list above it holds ONLY address rows; a served metro with no building
 * is a different species (Lupton p222 grouping) and gets this labeled band,
 * not a row. Fed by a "City, ST"-validated input; chips are removable; no
 * star / badge / details / Edit — none of the building-row chrome applies.
 * Data lives in bb_locations metro rows (create on add, discard on remove),
 * which is what the service_locations projection reads.
 */
import { useState } from 'react';
import { message } from 'antd';
import { confirmRemoveGathered, isConfirmedGathered } from '../confirmRemoveGathered';

import { useBrandBibleWizardStore, selectEntries } from '@/stores/brandBibleWizardStore';
import type { BbEntry } from '@/features/brand-bible-wizard/types';

import {
  COVERAGE_HINT,
  coverageRowKey,
  hasCoverageLabel,
  normalizeCoverageLabel,
} from './alsoServing';
import { isMetroCoverageRow } from './useLocationMerge';

export default function AlsoServingBand() {
  const locations = useBrandBibleWizardStore(selectEntries('locations'));
  const upsertEntry = useBrandBibleWizardStore((s) => s.upsertEntry);
  const discardEntry = useBrandBibleWizardStore((s) => s.discardEntry);
  // The tenant this component rendered for: every write names it, and the store refuses one for a tenant it has left.
  const bbTenant = useBrandBibleWizardStore((s) => s.tenantId) ?? '';
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const areas = locations.filter(isMetroCoverageRow);
  const labels = areas.map((row) => (row.metro ?? '').trim());

  async function add(raw: string) {
    const label = normalizeCoverageLabel(raw);
    if (!label) {
      setError(COVERAGE_HINT);
      return;
    }
    if (hasCoverageLabel(labels, label)) {
      setError(`${label} is already in the list`);
      setDraft('');
      return;
    }
    setSaving(true);
    try {
      await upsertEntry('locations', { location_key: coverageRowKey(label), metro: label }, undefined, bbTenant);
      setDraft('');
      setError(null);
    } catch (e: unknown) {
      message.error(`Failed to add area — ${e instanceof Error ? e.message : 'request failed'}`);
    } finally {
      setSaving(false);
    }
  }

  async function remove(row: BbEntry<'locations'>) {
    // D57: an area a gather found and a person confirmed is remembered once removed, so ask first.
    if (isConfirmedGathered(row) && !(await confirmRemoveGathered((row.metro ?? '').trim() || 'this area'))) return;
    try {
      await discardEntry('locations', row.id, bbTenant);
    } catch (e: unknown) {
      message.error(`Failed to remove area — ${e instanceof Error ? e.message : 'request failed'}`);
    }
  }

  return (
    <div
      data-testid="bb-also-serving-band"
      className="border-t border-border px-4 pt-2 pb-3"
    >
      <div className="flex items-baseline justify-between gap-3">
        <p className="text-[12px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
          Also serving
        </p>
        <span className="text-[11px] text-muted-foreground">
          {areas.length} {areas.length === 1 ? 'area' : 'areas'} · coverage without a building
        </span>
      </div>
      <div className="mt-1.5 flex flex-wrap items-center gap-2">
        {areas.map((row) => {
          const label = (row.metro ?? '').trim();
          return (
            <span
              key={row.id}
              data-testid={`bb-also-serving-chip-${coverageRowKey(label)}`}
              className="inline-flex items-center gap-1 rounded-[9px] border border-border bg-muted px-2 py-0.5 text-[12px] font-medium text-foreground"
            >
              {label}
              <button
                type="button"
                data-testid={`bb-also-serving-remove-${row.id}`}
                aria-label={`Remove ${label}`}
                onClick={() => void remove(row)}
                className="grid h-6 w-6 place-items-center border-0 bg-transparent p-0 text-muted-foreground hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-ring"
              >
                ×
              </button>
            </span>
          );
        })}
        <input
          data-testid="bb-also-serving-input"
          value={draft}
          disabled={saving}
          onChange={(e) => {
            setDraft(e.target.value);
            setError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
              e.preventDefault();
              void add(draft);
            } else if (e.key === 'Escape') {
              setError(null);
              setDraft('');
            }
          }}
          onBlur={() => {
            if (draft.trim()) void add(draft);
          }}
          placeholder='City, ST — e.g. Round Rock, TX'
          aria-invalid={error ? true : undefined}
          className={[
            'min-h-8 min-w-[180px] flex-1 rounded-[9px] border bg-card px-2 text-[13px]',
            'text-foreground outline-none placeholder:text-muted-foreground/60',
            'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-ring',
            error ? 'border-destructive' : 'border-input focus-visible:border-coral',
          ].join(' ')}
        />
      </div>
      {saving && (
        <p data-testid="bb-also-serving-saving" className="mt-1 text-[11px] text-muted-foreground">
          Saving…
        </p>
      )}
      {error && (
        <p data-testid="bb-also-serving-error" className="mt-1 text-[11px] text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}
